import React, { useState, useEffect } from "react";
import { 
  Mic, 
  Edit3, 
  Database, 
  Layers, 
  Users, 
  BarChart2, 
  Send, 
  Clock, 
  Play, 
  Pause, 
  Plus, 
  Trash2, 
  CheckCircle, 
  AlertCircle, 
  Sparkles, 
  FileText, 
  RefreshCw,
  Cpu,
  Globe,
  Radio
} from "lucide-react";

interface InnovationHubViewProps {
  onBackToHub?: () => void;
  language?: 'uk' | 'en' | 'pl';
}

export const InnovationHubView: React.FC<InnovationHubViewProps> = ({ onBackToHub, language = 'uk' }) => {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';

  const [activeTab, setActiveTab] = useState<
    "audio" | "canvas" | "mcp" | "satellite" | "debate" | "chart" | "telegram" | "monitors"
  >("canvas");

  // State 1: Audio Briefing
  const [audioTopic, setAudioTopic] = useState("Аналітичний аудит контрагента та супутниковий моніторинг");
  const [audioContent, setAudioContent] = useState("ТОВ 'Агро-Термінал' має стабільні показники. За даними супутника Sentinel-2, площа вирощування збільшилась на 14% у 2025-2026 рр.");
  const [audioBriefing, setAudioBriefing] = useState<any>(null);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);

  // State 2: Interactive Canvas
  const [canvasText, setCanvasText] = useState(`# ЗВІТ ПРО ГЛИБОКЕ ДОСЛІДЖЕННЯ
**Об'єкт:** ТОВ «ТЕХНО-АГРО» (ЄДРПОУ 24507442)
**Дата:** ${new Date().toLocaleDateString("uk-UA")}

### 1. Юридичний та Санкційний Аудит
За даними реєстрів РНБО України, ДПС та ЄДРПОУ — ризиків та санкційних обмежень не виявлено.

### 2. Аналіз Фінансових Показників
Компанія демонструє зростання чистого доходу на 24% за останній звітний період.

### 3. Рекомендації
Рекомендовано для співпраці та надання ліміту відстрочки платежу до 45 днів.`);
  const [canvasInstruction, setCanvasInstruction] = useState("");
  const [loadingCanvas, setLoadingCanvas] = useState(false);

  // State 3: MCP Connectors
  const [connectors, setConnectors] = useState<any[]>([]);
  const [newConnName, setNewConnName] = useState("");
  const [newConnUrl, setNewConnUrl] = useState("");
  const [testingConnId, setTestingConnId] = useState<string | null>(null);

  // State 4: Satellite Change Detection
  const [satLocation, setSatLocation] = useState("Алушта");
  const [satDate1, setSatDate1] = useState("2024-06-01");
  const [satDate2, setSatDate2] = useState("2025-06-01");
  const [satChangeResult, setSatChangeResult] = useState<any>(null);
  const [loadingSatChange, setLoadingSatChange] = useState(false);

  // State 5: Multi-Agent Debate
  const [debateTopic, setDebateTopic] = useState("Оцінка кредитного ризику та комплаєнсу постачальника");
  const [debateResult, setDebateResult] = useState<any>(null);
  const [loadingDebate, setLoadingDebate] = useState(false);

  // State 6: QuickChart
  const [chartType, setChartType] = useState("bar");
  const [chartUrl, setChartUrl] = useState<string | null>(null);
  const [loadingChart, setLoadingChart] = useState(false);

  // State 7: Telegram Companion
  const [tgReportTitle, setTgReportTitle] = useState("Звіт Deep Research Hub 2026");
  const [sendingTg, setSendingTg] = useState(false);
  const [tgStatusMsg, setTgStatusMsg] = useState("");

  // State 8: Scheduled Monitors
  const [monitors, setMonitors] = useState<any[]>([]);
  const [newMonName, setNewMonName] = useState("");
  const [newMonTarget, setNewMonTarget] = useState("");

  // Fetch initial MCP and Monitors data
  useEffect(() => {
    fetchConnectors();
    fetchMonitors();
  }, []);

  const fetchConnectors = async () => {
    try {
      const res = await fetch("/api/mcp/connectors");
      const data = await res.json();
      if (data.success) setConnectors(data.connectors);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMonitors = async () => {
    try {
      const res = await fetch("/api/scheduled-monitors");
      const data = await res.json();
      if (data.success) setMonitors(data.monitors);
    } catch (err) {
      console.error(err);
    }
  };

  // 1. Audio Briefing Handler
  const handleGenerateAudio = async () => {
    setLoadingAudio(true);
    try {
      const res = await fetch("/api/audio-briefing", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: audioTopic, content: audioContent })
      });
      const data = await res.json();
      if (data.success) {
        setAudioBriefing(data.briefing);
      }
    } catch (err) {
      alert("Помилка генерації подкасту");
    } finally {
      setLoadingAudio(false);
    }
  };

  // 2. Canvas Edit Handler
  const handleEditCanvas = async () => {
    if (!canvasInstruction.trim()) return;
    setLoadingCanvas(true);
    try {
      const res = await fetch("/api/canvas/edit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: canvasText, instruction: canvasInstruction })
      });
      const data = await res.json();
      if (data.success) {
        setCanvasText(data.updatedContent);
        setCanvasInstruction("");
      }
    } catch (err) {
      alert("Помилка редагування полотна");
    } finally {
      setLoadingCanvas(false);
    }
  };

  // 3. MCP Handlers
  const handleAddConnector = async () => {
    if (!newConnName.trim()) return;
    try {
      const res = await fetch("/api/mcp/connectors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newConnName, url: newConnUrl })
      });
      const data = await res.json();
      if (data.success) {
        setConnectors([...connectors, data.connector]);
        setNewConnName("");
        setNewConnUrl("");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteConnector = async (id: string) => {
    try {
      const res = await fetch(`/api/mcp/connectors/${id}`, { method: "DELETE" });
      const data = await res.json();
      if (data.success) {
        setConnectors(connectors.filter((c) => c.id !== id));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleTestConnector = async (id: string) => {
    setTestingConnId(id);
    try {
      const res = await fetch(`/api/mcp/connectors/${id}/test`, { method: "POST" });
      const data = await res.json();
      if (data.success) {
        setConnectors(
          connectors.map((c) =>
            c.id === id ? { ...c, status: data.status, lastPing: new Date().toISOString() } : c
          )
        );
      }
    } catch (err) {
      console.error(err);
    } finally {
      setTestingConnId(null);
    }
  };

  // 4. Satellite Change Detection Handler
  const handleRunSatChange = async () => {
    setLoadingSatChange(true);
    try {
      const res = await fetch("/api/satellite/change-detection", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ location: satLocation, date1: satDate1, date2: satDate2 })
      });
      const data = await res.json();
      if (data.success) {
        setSatChangeResult(data.changeDetection);
      }
    } catch (err) {
      alert("Помилка супутникового моніторингу змін");
    } finally {
      setLoadingSatChange(false);
    }
  };

  // 5. Multi-Agent Debate Handler
  const handleRunDebate = async () => {
    setLoadingDebate(true);
    try {
      const res = await fetch("/api/multi-agent/debate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ topic: debateTopic, draftContent: canvasText })
      });
      const data = await res.json();
      if (data.success) {
        setDebateResult(data.debate);
      }
    } catch (err) {
      alert("Помилка запуску ШІ-Колегії");
    } finally {
      setLoadingDebate(false);
    }
  };

  // 6. QuickChart Handler
  const handleGenerateChart = async () => {
    setLoadingChart(true);
    try {
      const res = await fetch("/api/generate-chart", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: chartType })
      });
      const data = await res.json();
      if (data.success) {
        setChartUrl(data.chartUrl);
      }
    } catch (err) {
      alert("Помилка генерації діаграми");
    } finally {
      setLoadingChart(false);
    }
  };

  // 7. Telegram Companion Handler
  const handleSendToTelegram = async () => {
    setSendingTg(true);
    setTgStatusMsg("");
    try {
      const res = await fetch("/api/telegram-companion/send-report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reportTitle: tgReportTitle, fileContent: canvasText, format: "md" })
      });
      const data = await res.json();
      if (data.success) {
        setTgStatusMsg(isPl ? "✅ Raport wysłano do Twojego Telegrama!" : isUk ? "✅ Звіт успішно відправлено у ваш Telegram!" : "✅ Report sent to Telegram!");
      } else {
        setTgStatusMsg(isPl ? "❌ Błąd wysyłania do Telegrama." : isUk ? "❌ Помилка відправки в Telegram." : "❌ Telegram send error.");
      }
    } catch (err) {
      setTgStatusMsg(isPl ? "❌ Błąd sieci przy wysyłaniu." : isUk ? "❌ Помилка мережі при відправці." : "❌ Network error.");
    } finally {
      setSendingTg(false);
    }
  };

  // 8. Scheduled Monitor Handlers
  const handleAddMonitor = async () => {
    if (!newMonName.trim() || !newMonTarget.trim()) return;
    try {
      const res = await fetch("/api/scheduled-monitors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newMonName, target: newMonTarget, interval: "24h" })
      });
      const data = await res.json();
      if (data.success) {
        setMonitors([...monitors, data.monitor]);
        setNewMonName("");
        setNewMonTarget("");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteMonitor = async (id: string) => {
    try {
      const res = await fetch(`/api/scheduled-monitors/${id}`, { method: "DELETE" });
      const data = await res.json();
      if (data.success) {
        setMonitors(monitors.filter((m) => m.id !== id));
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4 md:p-8 font-sans">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-800 pb-6">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-indigo-500 via-purple-600 to-pink-500 rounded-2xl shadow-lg shadow-purple-500/20">
              <Sparkles className="w-8 h-8 text-white animate-pulse" />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold bg-gradient-to-r from-white via-slate-200 to-indigo-300 bg-clip-text text-transparent">
                {isPl 
                  ? 'Centrum Innowacji 2026 (Deep Research Hub)' 
                  : isUk 
                  ? 'Центр Інновацій 2026 (Deep Research Hub)' 
                  : 'Innovation Center 2026 (Deep Research Hub)'}
              </h1>
              <p className="text-slate-400 text-sm mt-1">
                {isPl
                  ? 'Pakiet 8 autonomicznych modułów: Canvas, Podcast, MCP Connectors, Change Detection i Kolegium Eksperckie.'
                  : isUk
                  ? 'Набір з 8 автономних ШІ-модулів другого покоління: Canvas, Podcast, MCP Connectors, Change Detection та Експертна Колегія.'
                  : 'Suite of 8 autonomous second-generation modules: Canvas, Podcast, MCP, Change Detection, and Multi-Agent Debate.'}
              </p>
            </div>
          </div>
        </div>
        {onBackToHub && (
          <button
            onClick={onBackToHub}
            className="px-5 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl font-medium transition-all duration-200 border border-slate-700 flex items-center gap-2"
          >
            {isPl ? '← Powrót do Panelu Głównego' : isUk ? '← Назад до Головного Хабу' : '← Back to Main Hub'}
          </button>
        )}
      </div>

      {/* Tabs Navigation */}
      <div className="max-w-7xl mx-auto mb-8 overflow-x-auto pb-2">
        <div className="flex gap-2 min-w-max border-b border-slate-800 pb-3">
          <button
            onClick={() => setActiveTab("canvas")}
            className={`px-4 py-2.5 rounded-xl font-semibold text-sm flex items-center gap-2 transition-all ${
              activeTab === "canvas"
                ? "bg-indigo-600 text-white shadow-lg shadow-indigo-600/30"
                : "bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
            }`}
          >
            <Edit3 className="w-4 h-4" /> 🎨 Interactive Canvas
          </button>

          <button
            onClick={() => setActiveTab("audio")}
            className={`px-4 py-2.5 rounded-xl font-semibold text-sm flex items-center gap-2 transition-all ${
              activeTab === "audio"
                ? "bg-indigo-600 text-white shadow-lg shadow-indigo-600/30"
                : "bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
            }`}
          >
            <Mic className="w-4 h-4" /> 🎙️ Audio Podcast
          </button>

          <button
            onClick={() => setActiveTab("mcp")}
            className={`px-4 py-2.5 rounded-xl font-semibold text-sm flex items-center gap-2 transition-all ${
              activeTab === "mcp"
                ? "bg-indigo-600 text-white shadow-lg shadow-indigo-600/30"
                : "bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
            }`}
          >
            <Database className="w-4 h-4" /> 🌐 MCP Connectors
          </button>

          <button
            onClick={() => setActiveTab("satellite")}
            className={`px-4 py-2.5 rounded-xl font-semibold text-sm flex items-center gap-2 transition-all ${
              activeTab === "satellite"
                ? "bg-indigo-600 text-white shadow-lg shadow-indigo-600/30"
                : "bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
            }`}
          >
            <Layers className="w-4 h-4" /> 🛰️ Change Detection
          </button>

          <button
            onClick={() => setActiveTab("debate")}
            className={`px-4 py-2.5 rounded-xl font-semibold text-sm flex items-center gap-2 transition-all ${
              activeTab === "debate"
                ? "bg-indigo-600 text-white shadow-lg shadow-indigo-600/30"
                : "bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
            }`}
          >
            <Users className="w-4 h-4" /> 👥 {isPl ? 'Kolegium Ekspertów' : isUk ? 'Експертна Колегія' : 'Expert Debate'}
          </button>

          <button
            onClick={() => setActiveTab("chart")}
            className={`px-4 py-2.5 rounded-xl font-semibold text-sm flex items-center gap-2 transition-all ${
              activeTab === "chart"
                ? "bg-indigo-600 text-white shadow-lg shadow-indigo-600/30"
                : "bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
            }`}
          >
            <BarChart2 className="w-4 h-4" /> 📊 QuickChart
          </button>

          <button
            onClick={() => setActiveTab("telegram")}
            className={`px-4 py-2.5 rounded-xl font-semibold text-sm flex items-center gap-2 transition-all ${
              activeTab === "telegram"
                ? "bg-indigo-600 text-white shadow-lg shadow-indigo-600/30"
                : "bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
            }`}
          >
            <Send className="w-4 h-4" /> 📲 Telegram Companion
          </button>

          <button
            onClick={() => setActiveTab("monitors")}
            className={`px-4 py-2.5 rounded-xl font-semibold text-sm flex items-center gap-2 transition-all ${
              activeTab === "monitors"
                ? "bg-indigo-600 text-white shadow-lg shadow-indigo-600/30"
                : "bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
            }`}
          >
            <Clock className="w-4 h-4" /> ⏰ {isPl ? 'Zaplanowane Monitory' : isUk ? 'Автономні Монітори' : 'Scheduled Monitors'}
          </button>
        </div>
      </div>
        {/* 1. INTERACTIVE CANVAS */}
        {activeTab === "canvas" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-slate-100 flex items-center gap-2">
                  <Edit3 className="w-5 h-5 text-indigo-400" /> Полотно Звіту (Deep Research Canvas)
                </h3>
                <span className="text-xs text-emerald-400 bg-emerald-950/60 px-3 py-1 rounded-full border border-emerald-800/40">
                  ДСТУ 4163:2020 Ready
                </span>
              </div>
              <textarea
                value={canvasText}
                onChange={(e) => setCanvasText(e.target.value)}
                rows={16}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl p-4 text-slate-200 font-mono text-sm focus:outline-none focus:border-indigo-500 transition-all resize-y"
              />
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl flex flex-col justify-between">
              <div>
                <h4 className="text-lg font-bold text-slate-100 mb-3 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-400" /> ШІ-Редактор Полотна
                </h4>
                <p className="text-slate-400 text-sm mb-4">
                  Введіть інструкцію для ШІ (наприклад: "Спрости висновок", "Додай таблицю порівняння ризиків", "Зроби юридичне застереження"):
                </p>

                <textarea
                  value={canvasInstruction}
                  onChange={(e) => setCanvasInstruction(e.target.value)}
                  placeholder="Наприклад: Додай блок оцінки податкових ризиків..."
                  rows={4}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 text-sm focus:outline-none focus:border-indigo-500 mb-4"
                />

                <button
                  onClick={handleEditCanvas}
                  disabled={loadingCanvas || !canvasInstruction.trim()}
                  className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-xl font-bold transition-all flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/30"
                >
                  {loadingCanvas ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                  Виконати ШІ-редагування
                </button>
              </div>

              <div className="mt-6 pt-4 border-t border-slate-800 text-xs text-slate-500">
                💡 Підказка: будь-які зміни в Canvas можна миттєво зберегти та експортувати у нативний Word або надіслати в Telegram!
              </div>
            </div>
          </div>
        )}

        {/* 2. AUDIO PODCAST */}
        {activeTab === "audio" && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl">
            <h3 className="text-xl font-bold text-slate-100 mb-4 flex items-center gap-2">
              <Mic className="w-5 h-5 text-pink-400" /> AI Audio Briefing & Podcast Synthesizer
            </h3>
            <p className="text-slate-400 text-sm mb-6">
              Створення 2-хвилинного аудіо-подкасту між двома ШІ-ведучими (Олексій та Марія) за матеріалами будь-якого звіту.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Тема або Заголовок</label>
                <input
                  type="text"
                  value={audioTopic}
                  onChange={(e) => setAudioTopic(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 text-sm focus:outline-none focus:border-indigo-500 mb-4"
                />

                <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Зміст Звіту</label>
                <textarea
                  value={audioContent}
                  onChange={(e) => setAudioContent(e.target.value)}
                  rows={6}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 text-sm focus:outline-none focus:border-indigo-500 mb-4"
                />

                <button
                  onClick={handleGenerateAudio}
                  disabled={loadingAudio}
                  className="w-full py-3 bg-pink-600 hover:bg-pink-500 disabled:opacity-50 text-white rounded-xl font-bold transition-all flex items-center justify-center gap-2 shadow-lg shadow-pink-600/30"
                >
                  {loadingAudio ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Mic className="w-5 h-5" />}
                  Синтезувати ШІ-Подкаст
                </button>
              </div>

              <div className="bg-slate-950 border border-slate-800 rounded-xl p-5 flex flex-col justify-between">
                <div>
                  <h4 className="text-md font-bold text-slate-200 mb-3 flex items-center gap-2">
                    <Radio className="w-5 h-5 text-pink-400" /> Результат Аудіо-Саммарі
                  </h4>
                  {audioBriefing ? (
                    <div>
                      <div className="p-3 bg-slate-900 rounded-lg mb-4 border border-slate-800">
                        <span className="text-xs text-pink-400 font-bold block mb-1">📻 {audioBriefing.podcastTitle}</span>
                        <p className="text-xs text-slate-300">{audioBriefing.summary}</p>
                      </div>

                      <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                        {audioBriefing.dialogue?.map((item: any, idx: number) => (
                          <div key={idx} className="text-xs p-2.5 rounded-lg bg-slate-900 border border-slate-800/60">
                            <span className={`font-bold ${item.speaker === "Олексій" ? "text-indigo-400" : "text-purple-400"}`}>
                              {item.speaker}:
                            </span>{" "}
                            <span className="text-slate-300">{item.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-slate-500 text-sm italic py-12 text-center">
                      Натисніть кнопку ліворуч, щоб згенерувати підкаст...
                    </div>
                  )}
                </div>

                {audioBriefing && (
                  <button
                    onClick={() => setIsPlayingAudio(!isPlayingAudio)}
                    className="w-full mt-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 border border-slate-700"
                  >
                    {isPlayingAudio ? <Pause className="w-4 h-4 text-pink-400" /> : <Play className="w-4 h-4 text-emerald-400" />}
                    {isPlayingAudio ? "Пауза відтворення" : "Прослухати подкаст (Голосовий відтворювач)"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 3. MCP CONNECTORS */}
        {activeTab === "mcp" && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-100 flex items-center gap-2">
                <Database className="w-5 h-5 text-cyan-400" /> Model Context Protocol (MCP) Connectors
              </h3>
              <span className="text-xs text-cyan-400 bg-cyan-950/60 px-3 py-1 rounded-full border border-cyan-800/40">
                Sovereign AI Security
              </span>
            </div>
            <p className="text-slate-400 text-sm mb-6">
              Підключення відкритих та корпоративних баз даних (1С/BAS, PostgreSQL, Google Drive, Notion, SharePoint) для локального дослідження агентів.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <div className="lg:col-span-2 space-y-3">
                {connectors.map((conn) => (
                  <div key={conn.id} className="p-4 bg-slate-950 border border-slate-800 rounded-xl flex items-center justify-between">
                    <div>
                      <h5 className="font-bold text-slate-200 text-sm flex items-center gap-2">
                        <Globe className="w-4 h-4 text-cyan-400" /> {conn.name}
                      </h5>
                      <p className="text-xs text-slate-500 font-mono mt-1">{conn.url}</p>
                    </div>

                    <div className="flex items-center gap-3">
                      <span className="text-xs px-2.5 py-1 rounded-md bg-emerald-950 text-emerald-400 border border-emerald-800/40">
                        ● {conn.status}
                      </span>
                      <button
                        onClick={() => handleTestConnector(conn.id)}
                        disabled={testingConnId === conn.id}
                        className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-200 rounded-lg border border-slate-700 transition-all flex items-center gap-1"
                      >
                        {testingConnId === conn.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : "Тест"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-slate-950 border border-slate-800 rounded-xl p-5">
                <h4 className="text-sm font-bold text-slate-200 mb-3 flex items-center gap-2">
                  <Plus className="w-4 h-4 text-cyan-400" /> Додати MCP Сервер
                </h4>
                <input
                  type="text"
                  placeholder="Назва конектора..."
                  value={newConnName}
                  onChange={(e) => setNewConnName(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-800 rounded-lg p-2.5 text-xs text-slate-200 mb-3 focus:outline-none focus:border-cyan-500"
                />
                <input
                  type="text"
                  placeholder="mcp://host:port/database"
                  value={newConnUrl}
                  onChange={(e) => setNewConnUrl(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-800 rounded-lg p-2.5 text-xs text-slate-200 mb-4 focus:outline-none focus:border-cyan-500"
                />
                <button
                  onClick={handleAddConnector}
                  className="w-full py-2.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg text-xs font-bold transition-all shadow-lg shadow-cyan-600/30"
                >
                  Підключити MCP-сервер
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 4. SATELLITE CHANGE DETECTION */}
        {activeTab === "satellite" && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl">
            <h3 className="text-xl font-bold text-slate-100 mb-4 flex items-center gap-2">
              <Layers className="w-5 h-5 text-emerald-400" /> Multi-Temporal Change Detection (Супутникова Аналітика)
            </h3>
            <p className="text-slate-400 text-sm mb-6">
              Дистанційне порівняння та розрахунок зміни площ (NDVI, вирубка, нове будівництво) між довільними датами.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-1">Локація / Місто</label>
                <input
                  type="text"
                  value={satLocation}
                  onChange={(e) => setSatLocation(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 text-sm focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-1">Період 1 (Початковий)</label>
                <input
                  type="date"
                  value={satDate1}
                  onChange={(e) => setSatDate1(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 text-sm focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-1">Період 2 (Поточний)</label>
                <input
                  type="date"
                  value={satDate2}
                  onChange={(e) => setSatDate2(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 text-sm focus:outline-none focus:border-emerald-500"
                />
              </div>
            </div>

            <button
              onClick={handleRunSatChange}
              disabled={loadingSatChange}
              className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-xl font-bold transition-all mb-6 flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/30"
            >
              {loadingSatChange ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Layers className="w-5 h-5" />}
              Виконати Часовий Порівняльний Аналіз
            </button>

            {satChangeResult && (
              <div className="bg-slate-950 border border-slate-800 rounded-xl p-5">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-sm font-bold text-emerald-400">📊 Звіт про зміну площ</h4>
                  <span className="text-xs text-slate-400">Змінено: <strong>{satChangeResult.areaChangedSqM?.toLocaleString()} м²</strong> ({satChangeResult.changePercentage}%)</span>
                </div>
                <p className="text-xs text-slate-300 mb-4">{satChangeResult.summary}</p>

                <div className="space-y-2">
                  {satChangeResult.detectedChanges?.map((ch: any, idx: number) => (
                    <div key={idx} className="p-3 bg-slate-900 border border-slate-800/80 rounded-lg flex justify-between items-center text-xs">
                      <span className="font-semibold text-slate-200">{ch.type}</span>
                      <span className="text-emerald-400 font-mono">{ch.areaSqM?.toLocaleString()} м² ({ch.details})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 5. MULTI-AGENT DEBATE */}
        {activeTab === "debate" && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl">
            <h3 className="text-xl font-bold text-slate-100 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-400" /> Мультиагентне Рецензування («Експертна Колегія»)
            </h3>
            <p className="text-slate-400 text-sm mb-6">
              3-стадійний аналіз звітів трьома автономними агентами: Дослідник → Юрист/Критик → Головний Редактор.
            </p>

            <div className="mb-6">
              <label className="block text-xs font-semibold text-slate-400 mb-2">Тема або Об'єкт Аудиту</label>
              <input
                type="text"
                value={debateTopic}
                onChange={(e) => setDebateTopic(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 text-sm focus:outline-none focus:border-purple-500 mb-4"
              />

              <button
                onClick={handleRunDebate}
                disabled={loadingDebate}
                className="w-full py-3 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white rounded-xl font-bold transition-all flex items-center justify-center gap-2 shadow-lg shadow-purple-600/30"
              >
                {loadingDebate ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Users className="w-5 h-5" />}
                Запустити ШІ-Колегію
              </button>
            </div>

            {debateResult && (
              <div className="space-y-4">
                <div className="p-4 bg-purple-950/40 border border-purple-800/40 rounded-xl">
                  <span className="text-xs font-bold text-purple-300 block mb-1">🤝 Консенсус Колегії</span>
                  <p className="text-xs text-slate-200">{debateResult.finalConsensus}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {debateResult.stages?.map((st: any, idx: number) => (
                    <div key={idx} className="p-4 bg-slate-950 border border-slate-800 rounded-xl">
                      <span className="text-xs text-purple-400 font-bold block">{st.agent}</span>
                      <span className="text-[10px] text-slate-500 block mb-2">{st.role}</span>
                      <p className="text-xs text-slate-300">{st.findings}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 6. QUICKCHART */}
        {activeTab === "chart" && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl">
            <h3 className="text-xl font-bold text-slate-100 mb-4 flex items-center gap-2">
              <BarChart2 className="w-5 h-5 text-amber-400" /> Dynamic Visualizations & QuickChart Generator
            </h3>
            <p className="text-slate-400 text-sm mb-6">
              Генерація інфографіки та графіків у форматі PNG для нативного вбудовування у Word-звіти за ДСТУ.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-2">Тип Діаграми</label>
                <select
                  value={chartType}
                  onChange={(e) => setChartType(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 text-sm focus:outline-none focus:border-amber-500 mb-4"
                >
                  <option value="bar">Стовпчаста (Bar Chart)</option>
                  <option value="line">Лінійний Тренд (Line Chart)</option>
                  <option value="pie">Кругова (Pie Chart)</option>
                  <option value="radar">Радар Ризиків (Radar Chart)</option>
                </select>

                <button
                  onClick={handleGenerateChart}
                  disabled={loadingChart}
                  className="w-full py-3 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white rounded-xl font-bold transition-all flex items-center justify-center gap-2 shadow-lg shadow-amber-600/30"
                >
                  {loadingChart ? <RefreshCw className="w-5 h-5 animate-spin" /> : <BarChart2 className="w-5 h-5" />}
                  Згенерувати Діаграму
                </button>
              </div>

              <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 flex items-center justify-center min-h-[220px]">
                {chartUrl ? (
                  <img src={chartUrl} alt="QuickChart render" className="rounded-lg shadow-lg max-w-full" />
                ) : (
                  <span className="text-slate-500 text-sm italic">Натисніть кнопку ліворуч для генерації...</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 7. TELEGRAM COMPANION */}
        {activeTab === "telegram" && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl">
            <h3 className="text-xl font-bold text-slate-100 mb-4 flex items-center gap-2">
              <Send className="w-5 h-5 text-sky-400" /> Telegram Companion & Report Dispatcher
            </h3>
            <p className="text-slate-400 text-sm mb-6">
              Миттєве відправлення згенерованих аналітичних звітів у ваш персональний Telegram-чат.
            </p>

            <div className="max-w-xl space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-1">Назва Звіту</label>
                <input
                  type="text"
                  value={tgReportTitle}
                  onChange={(e) => setTgReportTitle(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 text-sm focus:outline-none focus:border-sky-500"
                />
              </div>

              <button
                onClick={handleSendToTelegram}
                disabled={sendingTg}
                className="w-full py-3 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white rounded-xl font-bold transition-all flex items-center justify-center gap-2 shadow-lg shadow-sky-600/30"
              >
                {sendingTg ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                Надіслати Звіт у Telegram
              </button>

              {tgStatusMsg && (
                <div className="p-3 bg-slate-950 border border-slate-800 rounded-lg text-xs font-semibold text-slate-200">
                  {tgStatusMsg}
                </div>
              )}
            </div>
          </div>
        )}

        {/* 8. SCHEDULED MONITORS */}
        {activeTab === "monitors" && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl">
            <h3 className="text-xl font-bold text-slate-100 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-teal-400" /> Autopilot & Scheduled Research Monitors
            </h3>
            <p className="text-slate-400 text-sm mb-6">
              Налаштування фонового автоматичного моніторингу супутників, тендерів та контрагентів за розкладом.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-3">
                {monitors.map((mon) => (
                  <div key={mon.id} className="p-4 bg-slate-950 border border-slate-800 rounded-xl flex items-center justify-between">
                    <div>
                      <h5 className="font-bold text-slate-200 text-sm">{mon.name}</h5>
                      <span className="text-xs text-teal-400 font-mono mt-1 block">📅 {mon.schedule} | 📌 {mon.target}</span>
                    </div>

                    <button
                      onClick={() => handleDeleteMonitor(mon.id)}
                      className="p-2 text-rose-400 hover:bg-rose-950/60 rounded-lg border border-rose-900/40 transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="bg-slate-950 border border-slate-800 rounded-xl p-5">
                <h4 className="text-sm font-bold text-slate-200 mb-3 flex items-center gap-2">
                  <Plus className="w-4 h-4 text-teal-400" /> Створити ШІ-Монітор
                </h4>
                <input
                  type="text"
                  placeholder="Назва завдання..."
                  value={newMonName}
                  onChange={(e) => setNewMonName(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-800 rounded-lg p-2.5 text-xs text-slate-200 mb-3 focus:outline-none focus:border-teal-500"
                />
                <input
                  type="text"
                  placeholder="Об'єкт моніторингу (Поле №4 / ЄДРПОУ / КВЕД)..."
                  value={newMonTarget}
                  onChange={(e) => setNewMonTarget(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-800 rounded-lg p-2.5 text-xs text-slate-200 mb-4 focus:outline-none focus:border-teal-500"
                />
                <button
                  onClick={handleAddMonitor}
                  className="w-full py-2.5 bg-teal-600 hover:bg-teal-500 text-white rounded-lg text-xs font-bold transition-all shadow-lg shadow-teal-600/30"
                >
                  Запустити ШІ-Монітор
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };
